import apiClient from '../utils/api';
import { LiteratureCase } from './caseManagementApi';

export interface ICSRExportData {
  caseId: number;
  patientInfo: {
    identifier?: string;
    age?: string;
    sex?: string;
    weight?: string;
    height?: string;
    pregnancyStatus?: string;
    medicalHistory?: string;
  };
  reporterInfo: {
    name?: string;
    type?: string;
    occupation?: string;
    contact?: string;
    country?: string;
    relationship?: string;
  };
  adverseEvent: {
    verbatim?: string;
    meddra?: string;
    startDate?: string;
    stopDate?: string;
    duration?: string;
    outcome?: string;
    seriousness?: string;
    severity?: string;
    causality?: string;
  };
  suspectProduct: {
    name?: string;
    generic?: string;
    dosageForm?: string;
    strength?: string;
    indication?: string;
    route?: string;
    dose?: string;
    startDate?: string;
    stopDate?: string;
    batchNumber?: string;
    expiryDate?: string;
    actionTaken?: string;
  };
  manufacturer: {
    name?: string;
    contact?: string;
  };
  caseNarrative: {
    narrative?: string;
    assessorComments?: string;
    caseType?: string;
    primarySourceCountry?: string;
    receivedDate?: string;
    reportedToAuthorityDate?: string;
    expedited?: boolean;
    followUpNeeded?: boolean;
  };
  sourceInfo: {
    articleTitle?: string;
    authors?: string;
    journal?: string;
    url?: string;
    doi?: string;
    aiScore?: number;
    extractionMethod: 'AI_LITERATURE_REVIEW';
  };
}

export const exportToICSR = async (literatureCase: LiteratureCase, articleDetails?: any): Promise<{ success: boolean; icsrId?: string; adverse_event_id?: number; message: string }> => {
  try {
    // Prepare ICSR export data from literature case
    const exportData: ICSRExportData = {
      caseId: literatureCase.id,
      patientInfo: {
        identifier: literatureCase.patient_identifier || undefined,
        age: literatureCase.patient_age || undefined,
        sex: literatureCase.patient_sex || undefined,
        weight: literatureCase.patient_weight || undefined,
        height: literatureCase.patient_height || undefined,
        pregnancyStatus: literatureCase.pregnancy_status || undefined,
        medicalHistory: literatureCase.medical_history || undefined,
      },
      reporterInfo: {
        name: literatureCase.reporter_name || undefined,
        type: literatureCase.reporter_type || 'Literature',
        occupation: literatureCase.reporter_occupation || undefined,
        contact: literatureCase.reporter_contact || undefined,
        country: literatureCase.reporter_country || undefined,
        relationship: literatureCase.reporter_relationship || undefined,
      },
      adverseEvent: {
        verbatim: literatureCase.event_verbatim || undefined,
        meddra: literatureCase.meddra_pt || undefined,
        startDate: literatureCase.ae_start_date || undefined,
        stopDate: literatureCase.ae_stop_date || undefined,
        duration: literatureCase.ae_duration || undefined,
        outcome: literatureCase.outcome || undefined,
        seriousness: literatureCase.seriousness || undefined,
        severity: literatureCase.severity || undefined,
        causality: literatureCase.causality || undefined,
      },
      suspectProduct: {
        name: literatureCase.suspect_product_name || undefined,
        generic: literatureCase.suspect_product_generic || undefined,
        dosageForm: literatureCase.dosage_form || undefined,
        strength: literatureCase.strength || undefined,
        indication: literatureCase.indication || undefined,
        route: literatureCase.route || undefined,
        dose: literatureCase.dose || undefined,
        startDate: literatureCase.product_start_date || undefined,
        stopDate: literatureCase.product_stop_date || undefined,
        batchNumber: literatureCase.batch_number || undefined,
        expiryDate: literatureCase.expiry_date || undefined,
        actionTaken: literatureCase.action_taken || undefined,
      },
      manufacturer: {
        name: literatureCase.manufacturer_name || undefined,
        contact: literatureCase.manufacturer_contact || undefined,
      },
      caseNarrative: {
        narrative: literatureCase.case_narrative || undefined,
        assessorComments: literatureCase.assessor_comments || undefined,
        caseType: literatureCase.case_type || 'Literature',
        primarySourceCountry: literatureCase.primary_source_country || undefined,
        receivedDate: literatureCase.received_date || undefined,
        reportedToAuthorityDate: literatureCase.reported_to_authority_date || undefined,
        expedited: literatureCase.expedited || false,
        followUpNeeded: literatureCase.follow_up_needed || false,
      },
      sourceInfo: {
        articleTitle: articleDetails?.article_title || undefined,
        authors: articleDetails?.article_authors_info || undefined,
        journal: articleDetails?.article_venue || undefined,
        url: articleDetails?.article_url || undefined,
        doi: articleDetails?.article_doi || undefined,
        aiScore: literatureCase.ai_score || 0,
        extractionMethod: 'AI_LITERATURE_REVIEW',
      }
    };

    // Call backend to register an ICSR record. ApiClient returns { data, error, status }
    const response = await apiClient.post<{ icsrId?: string; message?: string; adverse_event_id?: number }>('/api/literature/export-to-icsr', exportData);

    // Surface backend/network errors instead of blindly returning success
    if (!response || response.status < 200 || response.status >= 300 || response.error) {
      return {
        success: false,
        message: response?.error || `Export failed${response?.status ? ` (HTTP ${response.status})` : ''}`,
      };
    }

    const responseData = (response.data || {}) as { icsrId?: string; message?: string; adverse_event_id?: number };

    // Persist the last exported identifiers for dashboard auto-include
    try {
      if (responseData.icsrId) sessionStorage.setItem('LAST_EXPORT_RECORD_ID', responseData.icsrId);
      if (responseData.adverse_event_id) sessionStorage.setItem('LAST_EXPORT_AE_ID', String(responseData.adverse_event_id));
    } catch (e) {
      // ignore storage errors (e.g., privacy mode)
    }

    return {
      success: true,
      icsrId: responseData.icsrId,
      adverse_event_id: responseData.adverse_event_id,
      message: responseData.message || 'Successfully exported to ICSR management system',
    };
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const detail = data?.detail || data?.message;
    console.error('ICSR export error:', { status, data });
    return {
      success: false,
      message: detail || (status ? `Export failed (HTTP ${status})` : 'Failed to export to ICSR management system'),
    };
  }
};

export const getICSRExportStatus = async (caseId: number): Promise<{ exported: boolean; icsrId?: string; exportDate?: string }> => {
  try {
    const response = await apiClient.get(`/api/literature/icsr-export-status/${caseId}`);
    const responseData = response.data as { exported: boolean; icsrId?: string; exportDate?: string };
    return responseData;
  } catch (error) {
    console.error('Failed to get ICSR export status:', error);
    return { exported: false };
  }
};

export const generateICSRPreview = (literatureCase: LiteratureCase, articleDetails?: any): string => {
  const exportData: ICSRExportData = {
    caseId: literatureCase.id,
    patientInfo: {
      identifier: literatureCase.patient_identifier || undefined,
      age: literatureCase.patient_age || undefined,
      sex: literatureCase.patient_sex || undefined,
      weight: literatureCase.patient_weight || undefined,
      height: literatureCase.patient_height || undefined,
      pregnancyStatus: literatureCase.pregnancy_status || undefined,
      medicalHistory: literatureCase.medical_history || undefined,
    },
    reporterInfo: {
      name: literatureCase.reporter_name || undefined,
      type: literatureCase.reporter_type || 'Literature',
      occupation: literatureCase.reporter_occupation || undefined,
      contact: literatureCase.reporter_contact || undefined,
      country: literatureCase.reporter_country || undefined,
      relationship: literatureCase.reporter_relationship || undefined,
    },
    adverseEvent: {
      verbatim: literatureCase.event_verbatim || undefined,
      meddra: literatureCase.meddra_pt || undefined,
      startDate: literatureCase.ae_start_date || undefined,
      stopDate: literatureCase.ae_stop_date || undefined,
      duration: literatureCase.ae_duration || undefined,
      outcome: literatureCase.outcome || undefined,
      seriousness: literatureCase.seriousness || undefined,
      severity: literatureCase.severity || undefined,
      causality: literatureCase.causality || undefined,
    },
    suspectProduct: {
      name: literatureCase.suspect_product_name || undefined,
      generic: literatureCase.suspect_product_generic || undefined,
      dosageForm: literatureCase.dosage_form || undefined,
      strength: literatureCase.strength || undefined,
      indication: literatureCase.indication || undefined,
      route: literatureCase.route || undefined,
      dose: literatureCase.dose || undefined,
      startDate: literatureCase.product_start_date || undefined,
      stopDate: literatureCase.product_stop_date || undefined,
      batchNumber: literatureCase.batch_number || undefined,
      expiryDate: literatureCase.expiry_date || undefined,
      actionTaken: literatureCase.action_taken || undefined,
    },
    manufacturer: {
      name: literatureCase.manufacturer_name || undefined,
      contact: literatureCase.manufacturer_contact || undefined,
    },
    caseNarrative: {
      narrative: literatureCase.case_narrative || undefined,
      assessorComments: literatureCase.assessor_comments || undefined,
      caseType: literatureCase.case_type || 'Literature',
      primarySourceCountry: literatureCase.primary_source_country || undefined,
      receivedDate: literatureCase.received_date || undefined,
      reportedToAuthorityDate: literatureCase.reported_to_authority_date || undefined,
      expedited: literatureCase.expedited || false,
      followUpNeeded: literatureCase.follow_up_needed || false,
    },
    sourceInfo: {
      articleTitle: articleDetails?.article_title || undefined,
      authors: articleDetails?.article_authors_info || undefined,
      journal: articleDetails?.article_venue || undefined,
      url: articleDetails?.article_url || undefined,
      doi: articleDetails?.article_doi || undefined,
      aiScore: literatureCase.ai_score || 0,
      extractionMethod: 'AI_LITERATURE_REVIEW',
    }
  };

  // Generate a human-readable preview of the ICSR data
  return `ICSR EXPORT PREVIEW - Literature Case #${exportData.caseId}

SOURCE INFORMATION:
- Article: ${exportData.sourceInfo.articleTitle || 'N/A'}
- Authors: ${exportData.sourceInfo.authors || 'N/A'}
- Journal: ${exportData.sourceInfo.journal || 'N/A'}
- URL: ${exportData.sourceInfo.url || 'N/A'}
- AI Score: ${exportData.sourceInfo.aiScore}/3
- Extraction Method: ${exportData.sourceInfo.extractionMethod}

PATIENT INFORMATION:
- Identifier: ${exportData.patientInfo.identifier || 'N/A'}
- Age: ${exportData.patientInfo.age || 'N/A'}
- Sex: ${exportData.patientInfo.sex || 'N/A'}
- Weight: ${exportData.patientInfo.weight || 'N/A'}
- Height: ${exportData.patientInfo.height || 'N/A'}
- Pregnancy Status: ${exportData.patientInfo.pregnancyStatus || 'N/A'}
- Medical History: ${exportData.patientInfo.medicalHistory || 'N/A'}

REPORTER INFORMATION:
- Name: ${exportData.reporterInfo.name || 'N/A'}
- Type: ${exportData.reporterInfo.type || 'Literature'}
- Occupation: ${exportData.reporterInfo.occupation || 'N/A'}
- Country: ${exportData.reporterInfo.country || 'N/A'}
- Relationship: ${exportData.reporterInfo.relationship || 'N/A'}

ADVERSE EVENT:
- Event Term: ${exportData.adverseEvent.verbatim || 'N/A'}
- MedDRA PT: ${exportData.adverseEvent.meddra || 'N/A'}
- Start Date: ${exportData.adverseEvent.startDate || 'N/A'}
- Stop Date: ${exportData.adverseEvent.stopDate || 'N/A'}
- Duration: ${exportData.adverseEvent.duration || 'N/A'}
- Outcome: ${exportData.adverseEvent.outcome || 'N/A'}
- Seriousness: ${exportData.adverseEvent.seriousness || 'N/A'}
- Severity: ${exportData.adverseEvent.severity || 'N/A'}
- Causality: ${exportData.adverseEvent.causality || 'N/A'}

SUSPECT PRODUCT:
- Product Name: ${exportData.suspectProduct.name || 'N/A'}
- Generic Name: ${exportData.suspectProduct.generic || 'N/A'}
- Dosage Form: ${exportData.suspectProduct.dosageForm || 'N/A'}
- Strength: ${exportData.suspectProduct.strength || 'N/A'}
- Indication: ${exportData.suspectProduct.indication || 'N/A'}
- Route: ${exportData.suspectProduct.route || 'N/A'}
- Dose: ${exportData.suspectProduct.dose || 'N/A'}
- Start Date: ${exportData.suspectProduct.startDate || 'N/A'}
- Stop Date: ${exportData.suspectProduct.stopDate || 'N/A'}
- Batch Number: ${exportData.suspectProduct.batchNumber || 'N/A'}
- Expiry Date: ${exportData.suspectProduct.expiryDate || 'N/A'}
- Action Taken: ${exportData.suspectProduct.actionTaken || 'N/A'}

MANUFACTURER:
- Name: ${exportData.manufacturer.name || 'N/A'}
- Contact: ${exportData.manufacturer.contact || 'N/A'}

CASE NARRATIVE:
${exportData.caseNarrative.narrative || 'No case narrative available'}

ASSESSOR COMMENTS:
${exportData.caseNarrative.assessorComments || 'No assessor comments'}

CASE PROCESSING:
- Case Type: ${exportData.caseNarrative.caseType || 'Literature'}
- Primary Source Country: ${exportData.caseNarrative.primarySourceCountry || 'N/A'}
- Received Date: ${exportData.caseNarrative.receivedDate || 'N/A'}
- Reported to Authority Date: ${exportData.caseNarrative.reportedToAuthorityDate || 'N/A'}
- Expedited: ${exportData.caseNarrative.expedited ? 'Yes' : 'No'}
- Follow-up Needed: ${exportData.caseNarrative.followUpNeeded ? 'Yes' : 'No'}
`;
};

